export const en = {
  meta: {
    title: "Hadron Energy Poland | Lithium Battery Systems & Electric Outboard Drives",
    description: "Custom battery packs and electric outboard drives for boats, e-mobility, energy storage and drones — assembled, certified and serviced in Poland. Lithium power you can build on: engineered globally, backed in Europe.",
  },
  nav: { about: "About Us", products: "Battery Systems", specs: "Electric Outboards", tech: "Technology / R&D", contact: "Contact", quote: "Request a configuration" },
  topbar: { badge: "European Assembly & Warranty Hub" },
  hero: {
    eyebrow: "Lithium power you can build on — engineered globally, backed in Europe",
    l1: "Advanced Lithium Power",
    l2: "& Marine Electric",
    l3: "Propulsion Systems",
    sub: "Custom battery packs and electric outboard drives for boats, e-mobility, energy storage and drones. Assembled, certified and serviced in Poland",
    cta1: "Explore Product Line",
    cta2: "Contact European Hub",
    chip1: "Est. 2014 — global clean energy group",
    chip2: "CE · CB · UN38.3 · ISO 9001 · SGS",
    chip3: "4,000+ m² manufacturing footprint",
    scroll: "Scroll",
  },
  marquee: ["CE Certified", "CB Scheme", "UN 38.3", "ISO 9001", "SGS Verified", "One-Stop Power Solution", "4,000+ m² Facility", "Solid-State R&D", "Zero-Emission Propulsion", "Hadron Energy Poland"],
  synergy: {
    eyebrow: "02 — Global Synergy",
    heading: "One group. Three continents. A single power standard.",
    lead: "Hadron Energy Poland is the European assembly base, regional EU distribution centre and the official EU warranty & after-sales repair hub of the Hadron group — majority-owned by a Singaporean group that also owns Hadron China (Wuxi Hadron New Energy Tech Co., Ltd.).",
    n1t: "Wuxi, China",
    n1d: "R&D & production HQ — cell selection, materials research and full-scale pack manufacturing.",
    n2t: "Singapore",
    n2d: "Ownership group — global governance, capital and strategic direction.",
    n3t: "Gdów, Poland",
    n3d: "European assembly base — small battery pack lines, EU distribution and the official warranty & after-sales repair hub.",
    s1l: "LiFePO₄ cycles (ESS)",
    s2l: "Manufacturing footprint",
    s3l: "Peak amps — dirt bike kits",
    s4l: "Founded",
    osTitle: "One-stop power solution",
    os: ["Cell selection & grading", "Custom circuit & structural design", "Battery module & pack assembly", "BMS integration", "Charger & connector accessories"],
    certsLabel: "Quality & safety certifications",
    missionLabel: "Mission",
    visionLabel: "Vision",
    mission: "To empower businesses and consumers with innovative green technology solutions for a quality of life.",
    vision: "Become a global leader in electric aircraft and electric ship powertrain systems in Poland and Europe.",
    brandNote: "Hadron — like the tide: striving forward in the electric tide",
    partnerTag: "Design partner",
    partnerTitle: "A design partner, not a ready-product shop",
    partnerText: "We don't sell off-the-shelf boxes. We co-engineer your pack — cell selection, custom circuit and structural design, BMS software, mechanics and certification support — a power system built around your platform, not adapted to it.",
    supplyTag: "Supply-chain stability",
    supplyTitle: "Battery packs with non-Chinese components & cells",
    supplyText: "For projects that require a geopolitically resilient supply chain, we build packs with no Chinese components: cells sourced from Japan, Korea or Malaysia and electronics assembled locally in Poland — audit-friendly and export-ready.",
  },
  products: {
    eyebrow: "03 — Product Line",
    heading: "Power systems for water, land and home.",
    lead: "Four engineered platforms, one manufacturing standard — from zero-emission propulsion to residential energy storage.",
    cta: "Request a configuration",
    datasheet: "Download datasheet (PDF)",
    punch: ["Quiet, zero-emission power for every hull", "More range and punch, plug-and-play", "Batteries that talk to your thruster", "Storage that pays back for years", "A powertrain built around your mission"],
    items: [
      {
        tag: "Electric propulsion", title: "Electric Outboard Motors", img: "/images/outboard_studio.jpg",
        desc: "High-efficiency electric propulsion that replaces petrol engines on sailboats, fishing boats, workboats and dinghies — connected to the Hadron Marine App.",
        f: ["Zero-emission replacement for petrol engines", "For sailboats, fishing boats, workboats & dinghies", "Hadron Marine App: GPS range visualisation", "Digital gauges & wireless OTA updates", "Digital display: real-time power, runtime, battery & voltage", "One-button electric start — adjustable max power for kids & rental fleets"],
        chips: ["HD-X02", "HD-X05", "12–144 V"],
      },
      {
        tag: "E-Mobility", title: "E-Mobility & E-Dirt Bike Batteries", img: "/images/dirtbike_battery.jpg",
        desc: "Custom high-discharge packs in 48 V, 60 V, 72 V and 96 V — including upgrade power kits for Sur-Ron and Talaria dirt bikes, managed by a custom Bluetooth BMS app.",
        f: ["48 / 60 / 72 / 96 V high-discharge packs", "Sur-Ron & Talaria kits: 60 V 56 Ah / 72 V 50 Ah", "Up to 350 A continuous · 550 A peak", "QS8/QS10 connectors · Android/iOS BMS app"],
        chips: ["350 A cont.", "550 A peak", "QS8 / QS10"],
      },
      {
        tag: "Marine", title: "Marine & Electric Boat Batteries", img: "/images/marine_battery.jpg",
        desc: "Heavy-duty marine battery packs in NCM and LiFePO₄ chemistries up to 200 Ah, with smart BMS over CAN / RS485 and high-current busbars that run cool.",
        f: ["NCM / LiFePO₄ — up to 200 Ah", "Smart BMS: CAN & RS485 protocols", "High-current copper-aluminium busbars", "Low heat generation under load", "Smart BMS compatible with multiple thruster models", "Standardized modular design — configurable on demand"],
        chips: ["200 Ah", "CAN / RS485", "NCM · LiFePO₄"],
      },
      {
        tag: "Energy Storage", title: "ESS & Smart Home", img: "/images/ess_wall.jpg",
        desc: "Residential ESS from a 5 kWh wall unit to 20 kWh cabinet stacking in LiFePO₄, industrial & commercial racks at 38.4 V / 76.8 V, plus smart home power packs.",
        f: ["Residential: 5 kWh wall → 20 kWh stack", "LiFePO₄ · >5,000 cycles", "C&I racks: 38.4 V / 76.8 V, air-cooled", "Power packs for tools, curtains & appliances"],
        chips: ["5–20 kWh", ">5,000 cycles", "LiFePO₄"],
      },
      {
        tag: "Aerospace", title: "VTOL & Drone Powertrains", img: "/images/vtol_drone.jpg",
        desc: "High-speed electric motors for VTOL and drone aircraft — with customization services that adapt the powertrain to specific customer requirements.",
        f: ["High-speed motors for VTOL & drone aircraft", "Customization to specific customer needs", "Lithium battery · motor · ECU integration", "In a market growing 13.9% annually to 2029"],
        chips: ["VTOL", "Drones", "Battery · Motor · ECU"],
      },
    ],
  },
  specs: {
    eyebrow: "04 — Technical Data",
    heading: "Electric outboard drives, compared.",
    lead: "A reference line-up of Hadron electric outboard drives — from compact 1–2 HP auxiliaries to a 90 HP-equivalent flagship.",
    rows: { model: "Model", voltage: "Voltage", power: "Power input", equiv: "Equivalent output", rpm: "Max RPM", cooling: "Cooling", control: "Control", weight: "Weight" },
    m1: { name: "HD-X02", voltage: "12 / 24 V", power: "0.5–1.1 kW", equiv: "1–2 HP", rpm: "—", cooling: "Water cooled", control: "Back tiller", weight: "8 kg" },
    m2: { name: "HD-X05 · 20 HP", voltage: "48–72 V", power: "15 kW", equiv: "20 HP", rpm: "≤ 6,000", cooling: "Fresh water cooled", control: "—", weight: "30 kg" },
    m3: { name: "HD-X05 · 90 HP", voltage: "144 V", power: "66 kW", equiv: "90 HP", rpm: "≤ 5,500", cooling: "Fresh water cooled", control: "—", weight: "115 kg" },
    note: "Full datasheets, mounting options and battery pairing are available on request from the European hub.",
    marketsTitle: "Market momentum",
    markets: [
      { v: "$43.25 B", l: "Electric ship market by 2030 — up from $8.96 B in 2025" },
      { v: "$67.64 B", l: "Commercial drone market by 2029 — up from $24.5 B in 2024" },
      { v: "13.9 %", l: "Commercial drone market CAGR" },
    ],
    datasheet: "Download outboard datasheet (PDF)",
  },
  tech: {
    eyebrow: "05 — Technology & R&D",
    heading: "Materials science before marketing.",
    lead: "Research conducted at the Wuxi HQ flows through the group straight into every pack assembled in Poland.",
    cards: [
      { t: "Solid-State Research", d: "Garnet-type solid electrolytes for the next generation of high-safety lithium cells." },
      { t: "Phase Change Materials", d: "PCM-based thermal management that stabilises cells under extreme loads." },
      { t: "ALD-Al₂O₃", d: "Atomic layer deposition for high-safety lithium cells and longer service life." },
    ],
    powertrainTitle: "The e-powertrain, integrated",
    powertrain: [
      { t: "Lithium Battery", d: "High energy density with fast charging and safety." },
      { t: "Electric Motor", d: "High efficiency with advanced cooling." },
      { t: "Control Unit", d: "Advanced electronic control unit (ECU)." },
    ],
    imgCaption: "Pack assembly — Gdów, Poland",
    fcTitle: "The 4C production principles",
    fc: [
      { t: "Clean environments", d: "Controlled assembly conditions from cell to pack." },
      { t: "Correct operation", d: "Validated processes at every station, every shift." },
      { t: "Consistent products", d: "One quality standard across China, Singapore and Poland." },
      { t: "Continuous improvement", d: "Traceability and iteration built into every batch." },
    ],
  },
  contact: {
    eyebrow: "08 — Contact",
    heading: "Let's power your next product.",
    lead: "An engineer will reply with a proposed configuration within 2 working days — in Polish, English, German, French, Italian or Croatian.",
    addrLabel: "Address", phoneLabel: "EU Sales & Support", emailLabel: "Email",
    f: {
      name: "Full name", company: "Company", email: "Email", interest: "Product interest",
      interests: ["Electric outboard motors", "E-mobility & dirt bike batteries", "Marine & boat batteries", "ESS & smart home", "VTOL & drone powertrains", "Custom battery packs"],
      msg: "Message", submit: "Send inquiry", sending: "Sending…",
      attach: "Attach requirement file", attachSize: "max. 10 MB", attachBad: "Only PDF or CSV files up to 10 MB are allowed.",
      success: "Inquiry received — we will reply within 2 business days.",
      errorGeneric: "Something went wrong — please try again.",
      errRequired: "Please fill in your name, a valid email and a message.",
    },
  },
  stakes: {
    eyebrow: "01 — What's at stake",
    empathy: "Your product is only as reliable as the battery inside it.",
    sub: "You need a pack or drive that fits your voltage, your housing and your certification path.",
    lead2: "We know what it costs when power fails far from support. Buying from the other side of the world usually means long lead times, slow claims and no one to call when a unit fails in the field.",
    risk: "That risk lands on you — your customers, your warranty, your reputation.",
    items: ["No local support when a pack fails", "Warranty claims stuck abroad for months", "Supply-chain questions you can't answer in an audit", "Units failing in the field with no one nearby to fix them", "Returns and warranty costs eating your margin", "Projects blocked by supply-chain audits"],
    philosophy: "A European product deserves a supplier that stands behind it in Europe.",
  },

  plan: {
    eyebrow: "06 — How it works",
    heading: "Three steps to power you can rely on.",
    steps: [
      { t: "Tell us your application", d: "Voltage, capacity, space, certification needs — or just what your product has to do." },
      { t: "Get your configuration and sample", d: "Our engineers match cells, BMS and housing, and send a spec and prototype for testing." },
      { t: "Build with European backing", d: "Packs assembled and tested in Poland, with warranty and service from the same team." },
    ],
    success: "Products that run on the water, on the trail and in the air. Warranty and service handled in Europe, in weeks not months. A supply chain you can document and defend.",
    cta: "Request a configuration",
  },

  story: {
    eyebrow: "Case study — application story",
    heading: "From spec to European backing: a 60 V marine pack.",
    challengeLabel: "The challenge", challenge: "An electric-boat builder needed a 60 V pack that would talk to its thrusters, survive demanding duty cycles and come with documentation that survives an EU audit — without a support line on the other side of the planet.",
    buildLabel: "The build", build: "Hadron engineers specified an NCM 60 V 150 Ah (17S1P) configuration with a smart BMS over CAN / RS485, high-current copper-aluminium busbars and compatibility with multiple thruster models — assembled and tested at the Gdów hub, with non-Chinese cells available for supply-chain-sensitive projects.",
    resultLabel: "The result", result: "A pack that can be serviced, warranted and documented in Europe — with the same team handling the claim from the first call to the repaired unit.",
    chips: ["60 V · 150 Ah (17S1P)", "150 A continuous", "CAN / RS485", "Non-Chinese cells on request"],
    cta: "Request a configuration",
    note: "Reference configuration from the Hadron product range — your application gets the same treatment: engineered around your platform, backed in Europe.",
  },

  faq: {
    eyebrow: "07 — FAQ",
    heading: "Answers for engines and humans alike.",
    items: [
      { q: "Where are Hadron products made?", a: "R&D and full-scale production sit at the Wuxi headquarters in China; battery packs for the European market are assembled at our Gdów plant in Poland — which is also the EU distribution centre and the official warranty & after-sales repair hub." },
      { q: "Do you sell standard products or build custom packs?", a: "We are a design partner, not a ready-product shop: from cell selection and custom circuit & structural design to BMS software, mechanics and certification support — every pack is built around your platform." },
      { q: "Can you supply battery packs without Chinese components?", a: "Yes. For supply-chain-sensitive projects we build packs with no Chinese components — cells sourced from Japan, Korea or Malaysia and electronics assembled locally in Poland, audit-friendly and export-ready." },
      { q: "Which certifications do Hadron packs carry?", a: "Our quality and safety baseline is CE, CB, UN38.3, ISO 9001 and SGS verification across the one-stop production chain." },
      { q: "Which electric outboard models are available?", a: "HD-X02 (12/24 V, 0.5–1.1 kW, 1–2 HP equivalent, 8 kg, back tiller), HD-X05 20 HP (48–72 V, 15 kW, 30 kg) and HD-X05 90 HP (144 V, 66 kW, 115 kg) — with the Hadron Marine App for GPS range, digital gauges and OTA updates." },
      { q: "Do you support Sur-Ron and Talaria dirt bikes?", a: "Yes — upgrade power kits in 60 V 56 Ah and 72 V 50 Ah, up to 350 A continuous and 550 A peak discharge, QS8/QS10 connectors and a Bluetooth BMS app for Android and iOS." },
    ],
  },

  footer: {
    tagline: "Smart clean energy solutions — engineered in Wuxi, governed from Singapore, assembled and serviced in Gdów, Poland.",
    disclaimer: "Hadron Energy Poland sp. z o.o. is majority-owned by a Singaporean ownership group that also owns Hadron China (Wuxi Hadron New Energy Tech Co., Ltd.).",
    rights: "All rights reserved.",
    privacy: "Privacy Policy",
    imprint: "Imprint",
    hub: "European Assembly & Warranty Hub",
  },
};
